import { listsApi } from '../../api/lists-api';
import type { IListCountKey, IListQuery, ListCriteriaNode } from '../../api/lists-contracts';
import { useAdminData } from '../../components/admin-page';
import { signalsOf, type CardVariant, type IStateSignal, type SignalSpec } from '../../components/state-cards';
import { ASSIGNED_TO } from '../lists/editing';
import type { IWorkSignal } from '../lists/work-page/signals';

/**
 * The DD list cards' counts and live signals (SR-456): survey-api's `/lists/counts`, one read under
 * the queue's narrowing — each card's own leaves a key, and each signal the card's leaves with one
 * more condition, keyed `<card>.<signal>`. That condition is also what a pressed signal adds to the
 * page's filter (`DD_SIGNALS`, `work-page/signals.ts`); `base` holds the filter without them.
 */

const SCHEDULED = 'dd.scheduled_at';
const STATUS = 'dd.status';
const OUTREACH = 'dd.outreach_count';
const NEXT_MEETING_AT = 'dd.next_meeting_at';

type Kind = 'overdue' | 'unassigned' | 'no_outreach' | 'meetings_this_week';

const SIGNALS: Record<Kind, SignalSpec & { leaves: ListCriteriaNode[] }> = {
  // A booked call whose day has passed with the call still to make — the queue's Overdue by day.
  overdue: {
    key: 'overdue',
    label: 'overdue',
    tone: 'danger',
    leaves: [
      { field: SCHEDULED, op: 'before', value: 'today' },
      {
        op: 'or',
        children: [
          { field: STATUS, op: 'empty' },
          { field: STATUS, op: 'not_in', value: ['completed', 'pending_review'] }
        ]
      }
    ]
  },
  unassigned: { key: 'unassigned', label: 'unassigned', tone: 'info', leaves: [{ field: ASSIGNED_TO, op: 'empty' }] },
  no_outreach: {
    key: 'no_outreach',
    label: 'no outreach',
    tone: 'attention',
    leaves: [
      {
        op: 'or',
        children: [
          { field: OUTREACH, op: 'empty' },
          { field: OUTREACH, op: 'eq', value: 0 }
        ]
      }
    ]
  },
  // A1: a booking of any kind — the DD call, a follow-up, a round — today or in the six days after,
  // by the business day survey-api keeps.
  meetings_this_week: {
    key: 'meetings_this_week',
    label: 'meetings this week',
    tone: 'info',
    leaves: [{ field: NEXT_MEETING_AT, op: 'between', value: ['today', 'next_n_days:6'] }]
  }
};

/** Each signal as a filter of the page — what its signals' group may hold. */
export const DD_SIGNALS: readonly IWorkSignal[] = Object.values(SIGNALS).map(({ key, label, leaves }) => ({
  key,
  words: label,
  leaves
}));

/** Each card's signals, the one a compact card shows first; *No outreach yet* shows none compact,
 *  and *Ready* leads with *overdue* — a row there holds no booking, so it is rarely drawn — so the
 *  six compact cards hold one row at 1366 px. */
const BY_CARD: Record<string, Kind[]> = {
  all: ['unassigned', 'overdue', 'no_outreach', 'meetings_this_week'],
  not_started: ['unassigned', 'no_outreach', 'meetings_this_week'],
  in_progress: ['overdue', 'unassigned', 'meetings_this_week'],
  scheduled: ['overdue', 'unassigned', 'meetings_this_week'],
  no_outreach: ['unassigned'],
  call: ['overdue', 'unassigned']
};

const group = (leaves: readonly ListCriteriaNode[]) =>
  leaves.length === 0 ? null : { version: 1, op: 'and', children: [...leaves] };

/** A card's signals under the variant: every one detailed, the first compact and those `on`. */
function shownKinds(
  card: string,
  everyone: boolean,
  variant: CardVariant,
  on: (key: string) => boolean = () => false
): Kind[] {
  const kinds = (BY_CARD[card] ?? []).filter((kind) => everyone || kind !== 'unassigned');
  if (variant === 'detailed') return kinds;
  return kinds.filter((kind, index) => on(kind) || (index === 0 && card !== 'no_outreach'));
}

/** The cards' counts and signals by card, null until read. `base` is the queue's narrowing without
 *  a card's leaves; `unassigned` counts only where the page shows everyone's rows; `on` says a
 *  card's signal pressed, which a compact card shows too. */
export function useDdCards(
  cards: ReadonlyArray<{ value: string; leaves: ListCriteriaNode[] }>,
  base: Nullable<IListQuery>,
  everyone: boolean,
  variant: CardVariant,
  counted: string,
  on: (card: string, key: string) => boolean
): Nullable<{ counts: Record<string, number>; signals: Record<string, IStateSignal[]> }> {
  const read = useAdminData<Nullable<{ counted: string; counts: Record<string, number> }>>(
    async (signal) => {
      if (!base) return null;
      const keys: IListCountKey[] = cards.flatMap((card) => [
        { key: card.value, criteria: group(card.leaves) },
        ...shownKinds(card.value, everyone, 'detailed').map((kind) => ({
          key: `${card.value}.${kind}`,
          criteria: group([...card.leaves, ...SIGNALS[kind].leaves])
        }))
      ]);
      const query = { survey: base.survey, surveys: base.surveys, scope: base.scope, criteria: base.criteria };
      return { counted, counts: (await listsApi.counts({ query, counts: keys }, signal)).counts };
    },
    [counted, base === null]
  );
  if (read.data?.counted !== counted) return null;
  const { counts } = read.data;
  return {
    counts,
    signals: Object.fromEntries(
      cards.map((card) => [
        card.value,
        signalsOf(
          counts,
          `${card.value}.`,
          shownKinds(card.value, everyone, variant, (key) => on(card.value, key)).map((kind) => SIGNALS[kind])
        )
      ])
    )
  };
}
